import Vue from "vue";
import VueRouter from "vue-router";
import store from "@/store";
import { GET_GROUPS, LOGOUT, GET_TENANT_ALIAS } from "@/store/auth.module";
import { VERIFY_FEATURE_ASYNC } from "@/store/feature.module";
import * as Role from "@/services/PermissionsService.js";

const UNAUTHORIZED_PAGE = { name: "403" };

Vue.use(VueRouter);

const isAllowed = (permissions, featureName) => (to, from, next) => {
  new Promise(async resolve => {
    resolve(
      (await featureCheck(featureName)) && (await permissionsCheck(permissions))
    );
  }).then(authorized => {
    if (authorized) next();
    else next(UNAUTHORIZED_PAGE);
  });
};

const featureCheck = async featureName => {
  return (
    !featureName || (await store.dispatch(VERIFY_FEATURE_ASYNC, featureName))
  );
};

const permissionsCheck = async restrictedGroups => {
  const groups = await store.dispatch(GET_GROUPS);
  return verifyGroups(restrictedGroups, groups);
};

const verifyGroups = (restrictedGroups, userGroups) => {
  if (!restrictedGroups || !restrictedGroups.length) return true;
  return restrictedGroups.some(g => userGroups.includes(g));
};

const routes = [
  {
    path: "/",
    beforeEnter: (to, from, next) => {
      if (store.getters.isAuthenticated) {
        store.dispatch(GET_GROUPS).then(groups => {
          if (groups.includes(Role.ADMIN)) next("/administration");
          else next("/merchant");
        });
      } else {
        next({ name: "login" });
      }
    }
  },
  //ADMIN PAGES
  {
    path: "/administration",
    component: () =>
      import(/* webpackChunkName: "base" */ "@/views/theme/Base"),
    redirect: { name: "admin-dashboard" },
    beforeEnter: (to, from, next) => {
      store.dispatch(GET_GROUPS).then(groups => {
        if (groups.includes(Role.ADMIN)) next();
        else next(UNAUTHORIZED_PAGE);
      });
    },
    children: [
      {
        path: "merchantslisting",
        name: "merchants-management",
        component: () =>
          import(
            /* webpackChunkName: "chunkla" */ "@/views/pages/merchant/merchants/Merchants.vue"
          )
      },
      {
        path: "merchant-details/:merchantName",
        name: "merchant-details",
        component: () =>
          import(
            /* webpackChunkName: "chunkla" */ "@/views/pages/merchant/merchants/MerchantDetails.vue"
          )
      },
      {
        path: "dashboard",
        name: "admin-dashboard",
        component: () =>
          import(/* webpackChunkName: "chunkd" */ "@/views/pages/Dashboard.vue")
      },
      {
        path: "merchants",
        name: "merchants",
        component: () =>
          import(
            /* webpackChunkName: "chunkaa" */ "@/views/pages/merchant/ManageMerchants.vue"
          )
      },
      {
        path: "cardtypes",
        name: "managecardtypes",
        component: () =>
          import(
            /* webpackChunkName: "chunkaa" */ "@/views/pages/cardtypes/ManageCardTypes.vue"
          )
      },
      {
        path: "userlisting",
        name: "admin-userlisting",
        component: () =>
          import(
            /* webpackChunkName: "chunkul" */ "@/views/pages/user-services/UserListing.vue"
          )
      },
      {
        path: "activities/:email?",
        name: "admin-activity-log",
        component: () =>
          import(
            /* webpackChunkName: "chunkal" */ "@/views/pages/auditing/ActivityLog.vue"
          )
      },
      {
        path: "acquirers",
        name: "acquirers",
        component: () =>
          import(
            /* webpackChunkName: "chunkaa" */ "@/views/pages/admin/Acquirers.vue"
          )
      },
      {
        path: "settings",
        name: "admin-settings",
        component: () =>
          import(
            /* webpackChunkName: "chunkaa" */ "@/views/pages/admin/AdminSettings.vue"
          )
      }
    ]
  },
  //MERCHANT PAGES
  {
    path: "/merchant",
    redirect: { name: "dashboard" },
    component: () =>
      import(/* webpackChunkName: "base" */ "@/views/theme/Base"),
    beforeEnter: (to, from, next) => {
      store.dispatch(GET_TENANT_ALIAS).then(tenantAlias => {
        if (!!tenantAlias) next();
        else next(UNAUTHORIZED_PAGE);
      });
    },
    children: [
      {
        path: "/users",
        name: "userlisting",
        beforeEnter: isAllowed([Role.CAN_VIEW_USERS, Role.MERCHANT_ADMIN]),
        component: () =>
          import(
            /* webpackChunkName: "chunkul" */ "@/views/pages/user-services/UserListing.vue"
          )
      },
      {
        path: "/dashboard",
        name: "dashboard",
        component: () =>
          import(
            /* webpackChunkName: "chunkla" */ "@/views/pages/merchant/MerchantLandingPage.vue"
          )
      },
      {
        path: "/promocodes/:promocodeid?",
        name: "promocodes",
        beforeEnter: isAllowed(
          [Role.VIEW_PROMOCODES, Role.MERCHANT_ADMIN],
          Role.FEATURES.PROMOTIONAL_CODES
        ),
        component: () =>
          import(
            /* webpackChunkName: "chunkla" */ "@/views/pages/merchant/promoCodes/PromoCodes.vue"
          )
      },
      {
        path: "/transactions",
        name: "transactions",
        beforeEnter: isAllowed([
          Role.VIEW_TRANSACTIONS,
          Role.EXPORT_TRANSACTIONS,
          Role.SEARCH_TRANSACTIONS,
          Role.COLLECT_FUNDS,
          Role.PARTIAL_COLLECT_FUNDS,
          Role.REFUND_TRANSACTIONS,
          Role.MERCHANT_ADMIN
        ]),
        component: () =>
          import(
            /* webpackChunkName: "chunkta" */ "@/views/pages/merchant/Transactions.vue"
          )
      },
      {
        path: "/activities/:email?",
        name: "activity-log",
        beforeEnter: isAllowed([
          Role.VIEW_ACTIVITY_LOG,
          Role.EXPORT_ACTIVITY_LOG,
          Role.MERCHANT_ADMIN
        ]),
        component: () =>
          import(
            /* webpackChunkName: "chunkal" */ "@/views/pages/auditing/ActivityLog.vue"
          )
      },
      {
        path: "/settings",
        name: "merchant-settings",
        beforeEnter: isAllowed([
          Role.VIEW_MERCHANT_SETTINGS,
          Role.EDIT_MERCHANT_SETTINGS,
          Role.SET_CUSTOM_FIELDS,
          Role.SET_LOGO,
          Role.SET_BANNER,
          Role.MERCHANT_ADMIN
        ]),
        component: () =>
          import(
            /* webpackChunkName: "chunkset" */ "@/views/pages/merchant/MerchantSettings.vue"
          )
      },
      {
        path: "/paymentrequests/:option?/:subOption?",
        name: "payment-request",
        beforeEnter: isAllowed([
          Role.VIEW_PAYMENT_REQUESTS,
          Role.STANDARD_PAYMENT_REQUEST,
          Role.SEND_RECURRING_PAYMENT_REQUEST,
          Role.MERCHANT_ADMIN
        ]),
        component: () =>
          import(
            /* webpackChunkName: "chunkpr" */ "@/views/pages/merchant/paymentrequest/PaymentRequest.vue"
          )
      }
    ]
  },
  //AUTH PAGES
  {
    path: "/auth",
    component: () =>
      import(/* webpackChunkName: "chunkau" */ "@/views/pages/auth/Auth"),
    redirect: { name: "login" },
    children: [
      {
        name: "login",
        path: "/login",
        component: () =>
          import(/* webpackChunkName: "chunklo" */ "@/views/pages/auth/Login"),
        beforeEnter: (to, from, next) => {
          if (store.getters.isAuthenticated) {
            store.dispatch(GET_GROUPS).then(groups => {
              if (groups.includes(Role.ADMIN)) next("/administration");
              else if (groups.includes(Role.MERCHANT_ADMIN)) next("/merchant");
              else {
                store.dispatch(LOGOUT);
                next();
              }
            });
          } else {
            store.dispatch(LOGOUT);
            next();
          }
        }
      },
      {
        name: "forgot-password",
        path: "/forgotpassword",
        component: () =>
          import(
            /* webpackChunkName: "chunkfp" */ "@/views/pages/auth/ForgotPassword"
          )
      },
      {
        name: "reset-password",
        path: "/resetpassword",
        component: () =>
          import(
            /* webpackChunkName: "chunkrp" */ "@/views/pages/auth/ResetPassword"
          )
      },
      {
        name: "register",
        path: "/register",
        component: () =>
          import(
            /* webpackChunkName: "chunkreg" */ "@/views/pages/auth/Register"
          )
      },
      {
        name: "confirm-account",
        path: "/confirm/:email",
        component: () =>
          import(
            /* webpackChunkName: "chunkca" */ "@/views/pages/auth/ConfirmAccount"
          )
      }
    ]
  },
  //STAND-ALONE PAGES
  {
    path: "/payment/:id*",
    name: "payment",
    component: () =>
      import(
        /* webpackChunkName: "chunkpa" */ "@/views/pages/payment/Payment.vue"
      )
  },
  {
    path: "/recurringpayment/:id",
    name: "recurring-payment",
    component: () =>
      import(
        /* webpackChunkName: "chunkrpa" */ "@/views/pages/recurringpayment/RecurringPayment.vue"
      )
  },
  {
    path: "/receipt/:id",
    name: "receipt",
    component: () =>
      import(
        /* webpackChunkName: "chunkrec" */ "@/views/pages/payment/Receipt.vue"
      )
  },
  {
    path: "/logout",
    name: "logout",
    beforeEnter: (to, from, next) => {
      if (store.getters.isAuthenticated) {
        store.dispatch(LOGOUT);
      }
      next({ name: "login" });
    }
  },
  {
    // the 403 route, when an unauthorized page is requested
    path: "/403",
    name: "403",
    component: () =>
      import(
        /* webpackChunkName: "chunkee" */ "@/views/pages/error/Error-4.vue"
      )
  },
  {
    // the 404 route, when none of the above matches
    path: "/404",
    name: "404",
    component: () =>
      import(
        /* webpackChunkName: "chunkee" */ "@/views/pages/error/Error-1.vue"
      )
  },
  {
    path: "/about",
    name: "About",
    component: () =>
      import(/* webpackChunkName: "chunkab" */ "@/views/About.vue")
  },
  //GLOBAL NOT FOOUND
  {
    path: "*",
    redirect: "/404"
  }
];

const router = new VueRouter({
  mode: "history",
  routes,
  linkActiveClass: "link-active"
});

export default router;
